/**
 * المرحلة 1.35 — the one-tap WhatsApp link the desk sends a patient: the
 * voucher's soft copy, the file's link, the queue turn.
 *
 * The server writes every message as a wa.me link (Backend/lib/voucher.js
 * whatsappUrl): the recipient's number in its international form and the
 * text already written. On a phone wa.me opens the app. On the desk's
 * computer it stops on WhatsApp's own landing page first, so here the same
 * link becomes WhatsApp Web's: the chat with that number opens in the
 * browser where the desk's WhatsApp is signed in, the message in the box,
 * one Enter to send.
 *
 * Pure apart from `onComputer` (it reads the browser); tested in
 * Tests/suites/frontend/p135-center.test.mjs.
 */

/** The number and the text of a wa.me (or api.whatsapp.com / web.whatsapp.com) link; null for anything else. */
export function parseWhatsApp(url) {
  let u;
  try {
    u = new URL(String(url || ''));
  } catch {
    return null;
  }
  const host = u.hostname.replace(/^www\./, '');
  let phone = '';
  if (host === 'wa.me') phone = u.pathname.replace(/^\/+/, '').split('/')[0] || '';
  else if (host === 'api.whatsapp.com' || host === 'web.whatsapp.com') phone = u.searchParams.get('phone') || '';
  else return null;
  return { phone: phone.replace(/\D/g, ''), text: u.searchParams.get('text') || '' };
}

/** WhatsApp Web's address for the same chat and message. */
export function webUrl({ phone = '', text = '' } = {}) {
  const params = new URLSearchParams();
  if (phone) params.set('phone', String(phone).replace(/\D/g, ''));
  if (text) params.set('text', text);
  const query = params.toString();
  return `https://web.whatsapp.com/send${query ? `?${query}` : ''}`;
}

/** wa.me's address for the same chat and message (the phone's app). */
export function appUrl({ phone = '', text = '' } = {}) {
  const digits = String(phone || '').replace(/\D/g, '');
  return `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
}

/** Is this a computer (no touch-first pointer, no mobile user agent)? */
export function onComputer(win = typeof window === 'undefined' ? null : window) {
  if (!win) return true;
  const ua = (win.navigator && win.navigator.userAgent) || '';
  if (/Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile/i.test(ua)) return false;
  try {
    if (win.matchMedia && win.matchMedia('(pointer: coarse)').matches && !win.matchMedia('(any-pointer: fine)').matches) return false;
  } catch {
    /* an old browser: a computer */
  }
  return true;
}

/**
 * The link to open: WhatsApp Web on a computer, the app's wa.me on a phone.
 * Anything that is not a WhatsApp link is returned as it came.
 */
export function whatsappHref(url, { computer = onComputer() } = {}) {
  const parsed = parseWhatsApp(url);
  if (!parsed) return url || null;
  return computer ? webUrl(parsed) : appUrl(parsed);
}

/** The number as the desk reads it on the link: 01xxxxxxxxx for Egypt, else +<digits>. */
export function displayNumber(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('20') && digits.length === 12) return `0${digits.slice(2)}`;
  return `+${digits}`;
}
